import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";

const METHODS = [
  { v: "last", label: "Poslední cena", desc: "Použije se cena z nejnovějšího příjmu." },
  { v: "period_avg", label: "Průměr za období", desc: "Průměr cen v zadaném rozmezí dat." },
  { v: "total_avg", label: "Celkový průměr", desc: "Průměr všech evidovaných cen." },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/categories").then((r) => setSuppliers(r.data.suppliers || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return null;
  const up = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Nastavení uloženo");
    } catch {
      toast.error("Uložení se nezdařilo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Nastavení výpočtu" subtitle="Jak se mají napočítávat ceny surovin do kalkulací">
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="settings-save">
          <Save className="h-4 w-4 mr-2" /> Uložit
        </Button>
      </PageHeader>

      <div className="px-10 py-8 max-w-3xl space-y-6">
        <div className="bg-white border border-border rounded-xl p-8 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Cenový základ</Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {[
                { v: "netto", label: "Netto (bez DPH)" },
                { v: "brutto", label: "Brutto (s DPH)" },
              ].map((o) => (
                <button key={o.v} data-testid={`settings-base-${o.v}`}
                  onClick={() => up("price_base", o.v)}
                  className={`nav-link px-4 py-3 rounded-lg border text-sm font-medium text-left ${
                    s.price_base === o.v ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-muted"
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Metoda výpočtu ceny</Label>
            <div className="space-y-2 mt-3">
              {METHODS.map((m) => (
                <button key={m.v} data-testid={`settings-method-${m.v}`}
                  onClick={() => up("method", m.v)}
                  className={`nav-link w-full px-4 py-3 rounded-lg border text-left ${
                    s.method === m.v ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                  }`}>
                  <div className={`text-sm font-medium ${s.method === m.v ? "text-primary" : ""}`}>{m.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {s.method === "period_avg" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pf">Období od</Label>
                <Input id="pf" type="date" value={s.period_from || ""} data-testid="settings-period-from"
                  onChange={(e) => up("period_from", e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="pt">Období do</Label>
                <Input id="pt" type="date" value={s.period_to || ""} data-testid="settings-period-to"
                  onChange={(e) => up("period_to", e.target.value)} className="mt-2" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Omezit na dodavatele</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Volitelné — počítat ceny jen od konkrétního dodavatele.</p>
            <Select value={s.supplier_filter || "__all__"}
              onValueChange={(v) => up("supplier_filter", v === "__all__" ? null : v)}>
              <SelectTrigger data-testid="settings-supplier"><SelectValue placeholder="Všichni dodavatelé" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">Všichni dodavatelé</SelectItem>
                {suppliers.map((sup) => <SelectItem key={sup} value={sup}>{sup}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Nastavení platí jako výchozí pro nové položky v kalkulacích. U každé položky lze cenu ručně přepsat mimo toto nastavení.
        </p>
      </div>
    </div>
  );
}
